import type { Heading, TocNode } from '../types';

/**
 * Converts the flat heading list into a nested tree.
 *
 * Level jumps (H2 straight to H4) and documents that start at a deep level are
 * both tolerated -- headings simply attach to the nearest shallower ancestor.
 */
export const buildTocTree = (headings: Heading[]): TocNode[] => {
  const roots: TocNode[] = [];
  const stack: TocNode[] = [];

  for (const heading of headings) {
    const node: TocNode = { ...heading, children: [] };

    while (stack.length > 0 && stack[stack.length - 1].level >= node.level) {
      stack.pop();
    }

    if (stack.length === 0) {
      roots.push(node);
    } else {
      stack[stack.length - 1].children.push(node);
    }

    stack.push(node);
  }

  return roots;
};

/** Flattens a TOC tree back into document order. */
export const flattenToc = (nodes: TocNode[]): TocNode[] =>
  nodes.flatMap((node) => [node, ...flattenToc(node.children)]);

/** Restricts an outline to a heading-level window, e.g. H2-H4 only. */
export const filterHeadingLevels = (
  headings: Heading[],
  minLevel = 1,
  maxLevel = 6,
): Heading[] => headings.filter((h) => h.level >= minLevel && h.level <= maxLevel);